import React, { useState, useEffect } from 'react'
import {
    Box, Typography, IconButton, Button, TextField, ToggleButton, ToggleButtonGroup, Dialog, Select, MenuItem,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import supabase from '../../../../supabaseClient';

const pad = (n) => String(n).padStart(2, '0');
const todayStr = () => {
    const d = new Date();
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const CUSTOM_PERIOD_KEY = 'custom';
const MONTH_NAMES = [
    'Styczeń', 'Luty', 'Marzec', 'Kwiecień', 'Maj', 'Czerwiec',
    'Lipiec', 'Sierpień', 'Wrzesień', 'Październik', 'Listopad', 'Grudzień',
];
const monthNameOf = (isoMonth) => {
    const [y, m] = isoMonth.split('-').map(Number);
    return `${MONTH_NAMES[m - 1]} ${y}`;
};

const fmtPln = (v) =>
    `${(v || 0).toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} zł`;

const toNumber = (v) => {
    const n = Number(String(v).replace(',', '.'));
    return Number.isFinite(n) ? n : 0;
};

function AmountRow({ colorBar, title, subtitle, value, onChange, onQuickFill, quickLabel }) {
    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                border: '1px solid',
                borderColor: 'secondary.light',
                borderRadius: '10px',
                px: 2,
                py: 1.25,
                mb: 1.5,
            }}
        >
            <Box sx={{ width: 4, alignSelf: 'stretch', borderRadius: '2px', backgroundColor: colorBar }} />
            <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontWeight: 700, fontSize: '0.9rem' }}>{title}</Typography>
                <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>{subtitle}</Typography>
            </Box>
            <Button
                variant="outlined"
                size="small"
                onClick={onQuickFill}
                sx={{ borderColor: 'secondary.light', color: 'text.secondary', minWidth: 64 }}
            >
                {quickLabel}
            </Button>
            <TextField
                size="small"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                InputProps={{
                    endAdornment: <Typography sx={{ color: 'text.secondary', fontSize: '0.85rem', ml: 1 }}>zł</Typography>,
                    sx: { fontWeight: 700, fontSize: '1.05rem', width: 150 },
                }}
                inputProps={{ inputMode: 'decimal', style: { textAlign: 'right' } }}
            />
        </Box>
    );
}

function PaymentsModal({ open, onClose, employee, idConfig, unpaidPeriods = [], defaultPeriodKey, onSaved }) {
    const [selectedKey, setSelectedKey] = useState(defaultPeriodKey || unpaidPeriods[0]?.key || null);
    const [customMonthIndex, setCustomMonthIndex] = useState('');
    const [customYear, setCustomYear] = useState(String(new Date().getFullYear()));
    const customMonth = (customMonthIndex && String(customYear).length === 4)
        ? `${customYear}-${pad(customMonthIndex)}`
        : '';
    const [hoursAmount, setHoursAmount] = useState('0');
    const [overtimeAmount, setOvertimeAmount] = useState('0');
    const [leaveAmount, setLeaveAmount] = useState('0');
    const [date, setDate] = useState(todayStr());
    const [method, setMethod] = useState('cash');
    const [note, setNote] = useState('');
    const [saving, setSaving] = useState(false);

    const selectedPeriod = selectedKey === CUSTOM_PERIOD_KEY
        ? (customMonth ? {
            key: CUSTOM_PERIOD_KEY,
            label: monthNameOf(customMonth),
            monthDate: customMonth,
            hoursRemaining: 0, leaveRemaining: 0, otherRemaining: 0,
            remaining: 0, earned: 0, paid: 0,
        } : null)
        : (unpaidPeriods.find((p) => p.key === selectedKey) || null);

    useEffect(() => {
        if (!open) return;
        const initialKey = defaultPeriodKey || unpaidPeriods[0]?.key || null;
        setSelectedKey(initialKey);
        setCustomMonthIndex('');
        setCustomYear(String(new Date().getFullYear()));
        setDate(todayStr());
        setMethod('cash');
        setNote('');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    useEffect(() => {
        setHoursAmount(String((selectedPeriod?.hoursRemaining ?? 0).toFixed(2)));
        setLeaveAmount(String((selectedPeriod?.leaveRemaining ?? 0).toFixed(2)));
        setOvertimeAmount(String((selectedPeriod?.otherRemaining ?? 0).toFixed(2)));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedKey]);

    const hours = toNumber(hoursAmount);
    const overtime = toNumber(overtimeAmount);
    const leave = toNumber(leaveAmount);
    const sum = hours + overtime + leave;

    const selectedRemainingTotal = (selectedPeriod?.hoursRemaining ?? 0) + (selectedPeriod?.leaveRemaining ?? 0) + (selectedPeriod?.otherRemaining ?? 0);
    const coversPeriod = selectedPeriod
        ? Math.abs((hours + overtime + leave) - selectedRemainingTotal) < 0.005
        : false;

    const handleSave = async () => {
        if (sum <= 0) { alert('Podaj kwotę wypłaty.'); return; }
        if (!date) { alert('Wybierz datę wypłaty.'); return; }

        const periodMonth = selectedPeriod ? `${selectedPeriod.monthDate}-01` : null;
        const rows = [];
        if (hours > 0) rows.push({ type: 'hours', amount: hours });
        if (overtime > 0) rows.push({ type: 'overtime_bonus', amount: overtime });
        if (leave > 0) rows.push({ type: 'leave', amount: leave });

        setSaving(true);
        const { error } = await supabase.from('payments').insert(
            rows.map((r) => ({
                employee_id: employee.id,
                id_configuration: idConfig,
                amount: r.amount,
                type: r.type,
                period_month: periodMonth,
                payment_method: method,
                date,
                note,
            }))
        );
        setSaving(false);

        if (error) {
            console.log(error);
            alert('Błąd zapisu: ' + error.message);
            return;
        }
        onSaved?.();
        onClose();
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: '16px', overflow: 'hidden' } }}>
            <Box sx={{ backgroundColor: 'primary.main', color: 'primary.contrastText', px: 3, py: 2.5, position: 'relative' }}>
                <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.5px', opacity: 0.85, textTransform: 'uppercase' }}>
                    Rejestracja wypłaty
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', mt: 0.5 }}>
                    <Typography sx={{ fontSize: '1.4rem', fontWeight: 700 }}>
                        {employee?.name} {employee?.surname}
                    </Typography>
                    <Box sx={{ textAlign: 'right' }}>
                        <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.5px', opacity: 0.85, textTransform: 'uppercase' }}>
                            Do zapłaty
                        </Typography>
                        <Typography sx={{ fontSize: '1.4rem', fontWeight: 700 }}>
                            {fmtPln(selectedRemainingTotal)}
                        </Typography>
                    </Box>
                </Box>
                <IconButton
                    onClick={onClose}
                    sx={{ position: 'absolute', top: 16, right: 16, color: 'primary.contrastText', backgroundColor: 'rgba(255,255,255,0.15)' }}
                >
                    <CloseIcon fontSize="small" />
                </IconButton>
            </Box>

            <Box sx={{ px: 3, py: 3, backgroundColor: 'background.default' }}>
                <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.5px', color: 'text.secondary', textTransform: 'uppercase', mb: 1 }}>
                    Za jaki okres płacimy
                </Typography>
                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 3 }}>
                    {unpaidPeriods.map((p) => {
                        const active = p.key === selectedKey;
                        const total = p.hoursRemaining + p.leaveRemaining + (p.otherRemaining || 0);
                        return (
                            <Box
                                key={p.key}
                                onClick={() => setSelectedKey(p.key)}
                                sx={{
                                    flex: '1 1 200px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1.5,
                                    border: '1.5px solid',
                                    borderColor: active ? 'primary.main' : 'secondary.light',
                                    backgroundColor: active ? 'secondary.light' : 'background.paper',
                                    borderRadius: '10px',
                                    px: 2,
                                    py: 1.25,
                                    cursor: 'pointer',
                                }}
                            >
                                <Box
                                    sx={{
                                        width: 20,
                                        height: 20,
                                        borderRadius: '5px',
                                        border: '2px solid',
                                        borderColor: active ? 'primary.main' : 'secondary.light',
                                        backgroundColor: active ? 'primary.main' : 'transparent',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: 'primary.contrastText',
                                        fontSize: '0.8rem',
                                        flexShrink: 0,
                                    }}
                                >
                                    {active ? '✓' : ''}
                                </Box>
                                <Box sx={{ flex: 1 }}>
                                    <Typography sx={{ fontWeight: 700, fontSize: '0.9rem' }}>{p.label}</Typography>
                                    <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>
                                        {p.paid > 0.005 ? `zapłacono ${fmtPln(p.paid)} z ${fmtPln(p.earned)} · zaległy` : 'nic nie zapłacono'}
                                    </Typography>
                                </Box>
                                <Typography sx={{ fontWeight: 700, color: 'primary.main' }}>{fmtPln(total)}</Typography>
                            </Box>
                        );
                    })}

                    <Box
                        onClick={() => setSelectedKey(CUSTOM_PERIOD_KEY)}
                        sx={{
                            flex: '1 1 200px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                            border: '1.5px dashed',
                            borderColor: selectedKey === CUSTOM_PERIOD_KEY ? 'primary.main' : 'secondary.light',
                            backgroundColor: selectedKey === CUSTOM_PERIOD_KEY ? 'secondary.light' : 'background.paper',
                            borderRadius: '10px',
                            px: 2,
                            py: 1.25,
                            cursor: 'pointer',
                        }}
                    >
                        <Box sx={{ flex: 1 }}>
                            <Typography sx={{ fontWeight: 700, fontSize: '0.9rem' }}>Inny okres</Typography>
                            <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>
                                dowolny miesiąc, w tym bez godzin
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1 }} onClick={(e) => e.stopPropagation()}>
                            <Select
                                size="small"
                                displayEmpty
                                value={customMonthIndex}
                                onChange={(e) => { setCustomMonthIndex(e.target.value); setSelectedKey(CUSTOM_PERIOD_KEY); }}
                                sx={{ minWidth: 130 }}
                            >
                                <MenuItem value="" disabled>Miesiąc</MenuItem>
                                {MONTH_NAMES.map((name, i) => (
                                    <MenuItem key={name} value={i + 1}>{name}</MenuItem>
                                ))}
                            </Select>
                            <TextField
                                size="small"
                                type="number"
                                value={customYear}
                                onChange={(e) => { setCustomYear(e.target.value); setSelectedKey(CUSTOM_PERIOD_KEY); }}
                                sx={{ width: 90 }}
                                inputProps={{ style: { textAlign: 'center' } }}
                            />
                        </Box>
                    </Box>
                </Box>

                <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.5px', color: 'text.secondary', textTransform: 'uppercase', mb: 1.5 }}>
                    Kwota wypłaty — rozbij na składniki
                </Typography>

                <AmountRow
                    colorBar="#A84355"
                    title="Godziny pracy"
                    subtitle={`do zapłaty ${fmtPln(selectedPeriod?.hoursRemaining ?? 0)}`}
                    value={hoursAmount}
                    onChange={setHoursAmount}
                    onQuickFill={() => setHoursAmount(String((selectedPeriod?.hoursRemaining ?? 0).toFixed(2)))}
                    quickLabel="Całość"
                />

                <AmountRow
                    colorBar="#D4A373"
                    title="Nadgodziny / dodatki"
                    subtitle={`do zapłaty ${fmtPln(selectedPeriod?.otherRemaining ?? 0)}`}
                    value={overtimeAmount}
                    onChange={setOvertimeAmount}
                    onQuickFill={() => setOvertimeAmount(String((selectedPeriod?.otherRemaining ?? 0).toFixed(2)))}
                    quickLabel="Całość"
                />

                <AmountRow
                    colorBar="#B58253"
                    title="Urlop"
                    subtitle={`do zapłaty ${fmtPln(selectedPeriod?.leaveRemaining ?? 0)}`}
                    value={leaveAmount}
                    onChange={setLeaveAmount}
                    onQuickFill={() => setLeaveAmount(String((selectedPeriod?.leaveRemaining ?? 0).toFixed(2)))}
                    quickLabel="Całość"
                />

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', borderTop: '1px solid', borderColor: 'secondary.light', pt: 2, mt: 1 }}>
                    <Box>
                        <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.5px', color: 'text.secondary', textTransform: 'uppercase' }}>
                            Suma wypłaty
                        </Typography>
                        <Typography sx={{ fontSize: '0.78rem', color: coversPeriod ? 'success.main' : 'text.secondary' }}>
                            {selectedPeriod
                                ? (coversPeriod ? 'pokrywa całość zaznaczonego okresu' : 'częściowa spłata zaznaczonego okresu')
                                : 'wypłata bez przypisanego okresu'}
                        </Typography>
                    </Box>
                    <Typography sx={{ fontSize: '1.6rem', fontWeight: 700, color: 'primary.main' }}>
                        {fmtPln(sum)}
                    </Typography>
                </Box>

                <Box sx={{ display: 'flex', gap: 2, mt: 3, flexWrap: 'wrap' }}>
                    <Box sx={{ flex: 1, minWidth: 160 }}>
                        <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.5px', color: 'text.secondary', textTransform: 'uppercase', mb: 0.5 }}>
                            Data wypłaty
                        </Typography>
                        <TextField
                            type="date"
                            size="small"
                            fullWidth
                            value={date}
                            onChange={(e) => setDate(e.target.value)}
                            onClick={(e) => e.target.showPicker?.()}
                        />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 220 }}>
                        <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.5px', color: 'text.secondary', textTransform: 'uppercase', mb: 0.5 }}>
                            Forma
                        </Typography>
                        <ToggleButtonGroup
                            exclusive
                            fullWidth
                            value={method}
                            onChange={(e, v) => v && setMethod(v)}
                        >
                            <ToggleButton
                                value="cash"
                                sx={{
                                    '&.Mui-selected': { backgroundColor: 'primary.main', color: 'primary.contrastText' },
                                    '&.Mui-selected:hover': { backgroundColor: 'primary.dark' },
                                }}
                            >
                                Gotówka
                            </ToggleButton>
                            <ToggleButton
                                value="transfer"
                                sx={{
                                    '&.Mui-selected': { backgroundColor: 'primary.main', color: 'primary.contrastText' },
                                    '&.Mui-selected:hover': { backgroundColor: 'primary.dark' },
                                }}
                            >
                                Przelew
                            </ToggleButton>
                        </ToggleButtonGroup>
                    </Box>
                </Box>

                <Box sx={{ mt: 2 }}>
                    <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.5px', color: 'text.secondary', textTransform: 'uppercase', mb: 0.5 }}>
                        Opis
                    </Typography>
                    <TextField
                        fullWidth
                        size="small"
                        placeholder="np. tygodniówka 17–23.08, premia za trasę nocną"
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                    />
                </Box>

                <Box sx={{ display: 'flex', gap: 1.5, mt: 3 }}>
                    <Button variant="outlined" onClick={onClose} sx={{ borderColor: 'secondary.light', color: 'text.primary' }}>
                        Anuluj
                    </Button>
                    <Button
                        variant="contained"
                        disableElevation
                        fullWidth
                        disabled={saving || sum <= 0}
                        onClick={handleSave}
                    >
                        {saving ? 'Zapisywanie…' : `Zapisz wypłatę ${fmtPln(sum)}`}
                    </Button>
                </Box>
            </Box>
        </Dialog>
    );
}

export default PaymentsModal;
